import { useEffect, useState, useRef, lazy, Suspense } from "react";
import "@/App.css";
import "@/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { useTranslation } from "react-i18next";

// Eager load critical pages
import LandingPage from "@/pages/LandingPage";

// Lazy load other pages for better initial load performance
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const EmailAccountsPage = lazy(() => import("@/pages/EmailAccountsPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const GoogleCallbackPage = lazy(() => import("@/pages/GoogleCallbackPage"));

// Components - lazy load non-critical components
const SupportChatbot = lazy(() => import("@/components/SupportChatbot"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));

// Loading fallback component
const PageLoader = () => (
  <div className="loading-container">
    <div className="loading-spinner" />
    <p className="font-handwritten text-lg text-[#8D6E63]">Loading...</p>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Auth Callback Component - Handles session_id from Emergent OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          toast.error("Authentication failed - no session found");
          navigate("/");
          return;
        }

        const sessionId = sessionIdMatch[1];
        console.log("Processing session:", sessionId.substring(0, 10) + "...");

        // Exchange session_id for user data
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        if (response.data) {
          toast.success(`Welcome back, ${response.data.name}!`);
          navigate("/dashboard", { state: { user: response.data } });
        }
      } catch (error) {
        console.error("Auth error:", error);
        const errorMessage = error.response?.data?.detail || "Authentication failed. Please try again.";
        toast.error(errorMessage);
        navigate("/");
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="auth-loading" data-testid="auth-loading">
      <div className="loading-spinner" />
      <h2>Opening your memory box...</h2>
      <p className="font-handwritten">Connecting to your past</p>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // Otherwise, check with server
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        if (response.data) {
          setUser(response.data);
          setIsAuthenticated(true);
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/");
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="loading-container" data-testid="loading-container">
        <div className="loading-spinner" />
        <p className="font-handwritten text-lg text-[#8D6E63]">Loading your memories...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children({ user, setUser });
};

// App Router - Detects session_id synchronously before routes
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id - synchronous detection
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              {({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute>
              {() => <EmailAccountsPage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              {() => <SettingsPage />}
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

function App() {
  // Listen for PWA installation
  useEffect(() => {
    // Handle app installed event
    const handleAppInstalled = async (event) => {
      console.log('Memio PWA was installed');
      
      try {
        // Notify the backend about the installation
        await axios.post(`${API}/notifications/app-installed`, {
          user_agent: navigator.userAgent,
          referrer: document.referrer || 'Direct'
        }, {
          withCredentials: true
        });
        console.log('Installation notification sent');
      } catch (error) {
        console.error('Failed to send installation notification:', error);
      }
    };

    // Listen for the appinstalled event
    window.addEventListener('appinstalled', handleAppInstalled);

    // Cleanup
    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  return (
    <div className="App paper-texture">
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            fontFamily: "'Nunito', sans-serif",
          },
        }}
      />
      <BrowserRouter>
        <AppRouter />
        <Suspense fallback={null}>
          <SupportChatbot />
          <CookieConsent />
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
